import { EmploymentList } from './EmploymentList'
/**
 * PersonDrawer — right-hand employee profile drawer (contact details + status).
 * Extracted from people.tsx so org.tsx (name/avatar click on a tree card) can
 * open the same drawer instead of duplicating it.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApp, type Localized } from '../../i18n'
import { api } from '../../api/client'
import { canEditPeople } from '../../auth/roles'
import { isBirthdayToday } from '../../lib/format'
import { palette } from '../../theme/palette'
import { tone, type Tone } from '../../theme/tones'
import type { Profile } from '../../lib/types'
import { AnimatePresence, Avatar, Button, Icon, IconButton } from '../../ui'
import { Stack, Text, XStack, YStack } from 'tamagui'
import { PersonEditor } from './PersonEditor'
import { useMe } from './useUsers'

export type Status = 'online' | 'busy' | 'away' | 'offline'

export interface PersonVM {
  id: string
  name: Localized
  role: Localized
  deptLabel: Localized
  office: Localized
  status: Status
  email: string
  vacation: boolean
  vacationUntil: string | null
  deputyName: string | null
  birthday: boolean
}

const STATUS_TONE: Record<Status, Tone> = { online: 'green', busy: 'red', away: 'gold', offline: 'slate' }

export const liveVM = (u: Profile): PersonVM => ({
  id: u.id,
  name: u.fullName,
  role: u.jobTitle ?? '',
  deptLabel: u.department ?? '—',
  office: [u.location?.city, u.location?.name].filter(Boolean).join(' · '),
  status: u.presence ?? 'offline',
  email: u.email,
  vacation: u.onVacation === true,
  vacationUntil: u.vacationUntil ?? null,
  deputyName: u.deputy?.fullName ?? null,
  birthday: isBirthdayToday(u.birthday),
})

function DetailRow({ icon, label, children }: { icon: string; label: string; children: React.ReactNode }) {
  return (
    <XStack gap={12} paddingVertical={11} borderBottomWidth={1} borderColor="$borderSecondary">
      <Icon name={icon} size={16} color="$tertiary" />
      <YStack flex={1} minWidth={0}>
        <Text fontSize={11} color="$muted">
          {label}
        </Text>
        <Stack marginTop={1}>
          {typeof children === 'string' ? (
            <Text fontSize={13} color="$color">
              {children}
            </Text>
          ) : (
            children
          )}
        </Stack>
      </YStack>
    </XStack>
  )
}

export function PersonDrawer({
  p,
  onClose,
}: {
  p: PersonVM | null
  onClose: () => void
}) {
  const { t, l } = useApp()
  const tn = tone(STATUS_TONE[p?.status ?? 'offline'])
  // Список/оргдерево не несут zupAssignment (только карточка GET /users/:id) — дотягиваем на открытии.
  // Кэшируется react-query по ['user', id]: повторное открытие того же — мгновенно.
  const { data: full } = useQuery({
    queryKey: ['user', p?.id],
    queryFn: () => api.user(p!.id),
    enabled: !!p,
  })
  const zup = full?.zupAssignment ?? null
  // HR/admin only — сам дровер решает, показывать ли «Редактировать» и держит id редактора.
  const { data: me } = useMe()
  const canEdit = canEditPeople(me)
  const [editId, setEditId] = useState<string | null>(null)
  // AnimatePresence keeps the tree alive while the drawer slides out on close.
  return (
    <>
    <AnimatePresence>
      {p && (
    <Stack key="person-drawer" position="absolute" top={0} left={0} right={0} bottom={0} zIndex={100}>
      <Stack
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={palette.scrimSoft}
        onPress={onClose}
        animation="quicker"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        opacity={1}
      />
      <YStack
        animation="quicker"
        enterStyle={{ x: 420, opacity: 0 }}
        exitStyle={{ x: 420, opacity: 0 }}
        x={0}
        opacity={1}
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        width={400}
        maxWidth="100%"
        backgroundColor="$background"
        borderLeftWidth={1}
        borderColor="$borderSecondary"
        shadowColor={palette.shadow}
        shadowOpacity={0.18}
        shadowRadius={24}
        shadowOffset={{ width: -8, height: 0 }}
      >
        {/* banner */}
        <Stack height={96} backgroundColor="$primary">
          <IconButton onPress={onClose} position="absolute" top={12} right={12}>
            <Icon name="x" size={18} color="$onPrimary" />
          </IconButton>
        </Stack>

        <YStack paddingHorizontal={24} paddingBottom={24} marginTop={-36}>
          <Avatar name={t(p.name)} seed={p.id} size="xl" status={p.status} />
          <Text fontSize={20} fontWeight="700" color="$color" marginTop={12}>
            {t(p.name)}
          </Text>
          <Text fontSize={13} color="$muted">
            {t(p.role)}
          </Text>

          {/* HR-правка карточки — только для hr/admin (UI-гейт; бэк проверяет роль сам) */}
          {canEdit && (
            <Button
              block
              marginBottom={16}
              icon={<Icon name="pencil" size={14} color="$primary" />}
              onPress={() => setEditId(p.id)}
            >
              {l('pe.edit')}
            </Button>
          )}

          <EmploymentList jobs={full?.employments} conflict={full?.employmentSelectionRequired} />
          <DetailRow icon="building-2" label={l('people.dept')}>
            {t(p.deptLabel)}
          </DetailRow>
          {/* Живое назначение из 1С (zup_org_assignment) — только когда есть */}
          {!full?.employments?.length && !!zup?.position && (
            <DetailRow icon="briefcase" label={l('people.zupPosition')}>
              {zup.position}
            </DetailRow>
          )}
          {!full?.employments?.length && !!zup?.unit && (
            <DetailRow icon="building-2" label={l('people.zupUnit')}>
              {zup.unit}
            </DetailRow>
          )}
          {!!t(p.office) && (
            <DetailRow icon="map-pin" label={l('people.office')}>
              {t(p.office)}
            </DetailRow>
          )}
          <DetailRow icon="mail" label="Email">
            {p.email || '—'}
          </DetailRow>
          <DetailRow icon="circle-dot" label={l('req.status')}>
            <XStack
              backgroundColor={tn.soft}
              borderRadius={999}
              paddingHorizontal={9}
              paddingVertical={3}
              alignSelf="flex-start"
              alignItems="center"
              gap={6}
            >
              <Stack width={7} height={7} borderRadius={999} backgroundColor={tn.solid} />
              <Text fontSize={11} fontWeight="700" color={tn.text}>
                {l(`status.${p.status}`)}
              </Text>
            </XStack>
          </DetailRow>
        </YStack>
      </YStack>
    </Stack>
      )}
    </AnimatePresence>
    <PersonEditor id={editId} onClose={() => setEditId(null)} />
    </>
  )
}
